/**
 * Canonical chart palette — theme-aware.
 *
 * The hex values here MUST match the corresponding CSS custom properties in
 * `web/src/app/globals.css` for both `:root` (light) and the dark-mode block.
 * Treat this file as the source of truth for any chart code that needs to
 * pass colour strings to Recharts (or anywhere else a CSS variable can't be
 * used directly).
 *
 * Chart components are client-bound (Recharts is client-only) and read
 * `usePrefersDark()` to pick a palette; non-chart consumers (page-level
 * legend swatches, server-rendered cards) should reach for the matching
 * `var(--token)` in CSS instead of importing this module.
 *
 * Token semantics — see web/COMPONENTS.md for the canonical reference.
 */

const lightPalette = {
  /** Highlighted thing / brand accent / city tax-base contribution. Use sparingly. */
  signal: "#c33013",
  /** Primary positive: community emissions, federal grants, primary categorical bar. */
  teal: "#0f766e",
  /** Secondary positive: external (provincial+federal) grants, secondary categorical. */
  cyan: "#0891b2",
  /** Deeper green: corporate operations, alt positive when teal is taken. */
  moss: "#3f5a2e",
  /** Mixed-source category, capital expenditure, blended buckets. */
  violet: "#7c3aed",
  /** Official ceiling / operational limit / hard cap. */
  azure: "#1d4ed8",
  /** Financial-with-strings: federal loan, licence cap, regulatory ceiling. */
  ochre: "#a16207",
  /** Loss, warning, non-revenue water, negative quantity. */
  brick: "#c41e1e",
  /** Low-attention category: default fill, "everything else" buckets. */
  slate: "#94a3b8",
  /** Reference lines, axis labels, neutral text on charts. */
  ink: "#3a3a37",
} as const;

const darkPalette = {
  signal: "#ff6b3d",
  teal: "#5eead4",
  cyan: "#67e8f9",
  moss: "#94c89c",
  violet: "#a78bfa",
  azure: "#93c5fd",
  ochre: "#f5b842",
  brick: "#f87171",
  slate: "#b6c2cf",
  /** Maps to --ink-2 dark — chart axis/reference text on dark bg. */
  ink: "#d5d5d0",
} as const;

export type PaletteToken = keyof typeof lightPalette;
export type Palette = Record<PaletteToken, string>;

/**
 * Backward-compatible named export — light-mode hexes. Existing callers
 * reading `palette.X` continue to work and render in light hues regardless
 * of system preference. New chart code should prefer `getPalette(isDark)`
 * paired with `usePrefersDark()` so colours flip with the theme.
 *
 * Type is `typeof lightPalette` (readonly literal), not the wider `Palette`,
 * so accidental mutation is a compile error and the literal hex values are
 * preserved for callers that want them.
 */
export const palette = lightPalette;

export function getPalette(isDark: boolean): Palette {
  return isDark ? darkPalette : lightPalette;
}

/**
 * Vintage palette — softer, lighter, print-poster register.
 *
 * Used as full-card backgrounds in editorial contexts (FeatureStat,
 * BentoStat, hero bento blocks) and as a parallel chart palette where a
 * chart sits next to those blocks and should read editorially rather
 * than diagnostically. The diagnostic `palette` above stays the default
 * for analytical charts; reach for `vintage` only when the chart is part
 * of a magazine-bento composition or visually adjacent to one.
 *
 * No dark variant yet — vintage register is a one-off editorial choice
 * that is currently scoped to light-mode bento cards. If/when an
 * editorial chart in dark mode appears, add a `darkVintage` here and
 * mirror it in CSS vars.
 *
 * Tokens carry the same kind of civic meaning as the diagnostic palette
 * — they're a *register* swap, not a meaning swap. Each maps to its
 * diagnostic equivalent below; if you find yourself wanting both at
 * once in the same view, that's a sign to pick one register and commit.
 *
 *   coral   ↔ signal   (civic alarm / cost-to-citizen)
 *   sky     ↔ cyan     (external / institutional inflow)
 *   mustard ↔ ochre    (city tax money / financial-with-strings)
 *   sage    ↔ moss     (positive environmental change)
 */
export const vintage = {
  /** Civic alarm / cost-to-citizen / loud-attention block. */
  coral: "#ff5d2e",
  /** External funds / institutional inflow / "what came in." */
  sky: "#7eb6d6",
  /** City tax money / financial / "what we paid." */
  mustard: "#e6c64f",
  /** Positive environmental change / improving trend. */
  sage: "#b8c89c",
} as const;

export type VintageToken = keyof typeof vintage;
